import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  KeyRound,
  Loader2,
  Search,
  ShieldAlert,
  Trash2,
  Wifi,
} from "lucide-react";

async function apiJson(url, options = {}) {
  const response = await fetch(url, { credentials: "include", ...options });
  const text = await response.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("Risposta non valida dal server.");
  }
  if (!response.ok || !data.ok) {
    throw new Error(data.error || data.message || "Operazione non riuscita.");
  }
  return data;
}

function formatDate(value) {
  if (!value) return "mai";
  const normalized = String(value).includes("T") ? value : String(value).replace(" ", "T");
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("it-IT");
}

export default function FcicTokensAdmin({ isSuper }) {
  const [aziende, setAziende] = useState([]);
  const [tokens, setTokens] = useState({});
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [feedback, setFeedback] = useState({});
  const [importing, setImporting] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!isSuper) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiJson("/api/admin/fcic_tokens_list.php");
      setAziende(Array.isArray(data.aziende) ? data.aziende : []);
    } catch (err) {
      setError(err.message || "Errore caricamento token FCIC.");
    } finally {
      setLoading(false);
    }
  }, [isSuper]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return aziende;
    return aziende.filter((azienda) =>
      [azienda.nome, azienda.fcic_client_id, azienda.fcic_plan_code, azienda.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [aziende, query]);

  const setWorking = (aziendaId, action) =>
    setBusy((current) => ({ ...current, [aziendaId]: action }));

  const updateRow = (aziendaId, patch) =>
    setAziende((current) =>
      current.map((azienda) =>
        Number(azienda.id) === Number(aziendaId) ? { ...azienda, ...patch } : azienda
      )
    );

  async function saveToken(azienda) {
    const token = (tokens[azienda.id] || "").trim();
    if (!token) {
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "error", text: "Inserisci un token." } }));
      return;
    }
    setWorking(azienda.id, "save");
    try {
      const data = await apiJson("/api/admin/fcic_token_save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ azienda_id: azienda.id, action: "save", token }),
      });
      updateRow(azienda.id, {
        token_configured: true,
        token_hint: data.token_hint,
        fcic_last_sync: new Date().toISOString(),
      });
      setTokens((current) => ({ ...current, [azienda.id]: "" }));
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "success", text: data.message } }));
    } catch (err) {
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "error", text: err.message } }));
    } finally {
      setWorking(azienda.id, null);
    }
  }

  async function removeToken(azienda) {
    if (!window.confirm(`Rimuovere il token FCIC di ${azienda.nome}?`)) return;
    setWorking(azienda.id, "remove");
    try {
      const data = await apiJson("/api/admin/fcic_token_save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ azienda_id: azienda.id, action: "remove" }),
      });
      updateRow(azienda.id, { token_configured: false, token_hint: null });
      setTokens((current) => ({ ...current, [azienda.id]: "" }));
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "success", text: data.message } }));
    } catch (err) {
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "error", text: err.message } }));
    } finally {
      setWorking(azienda.id, null);
    }
  }

  async function testToken(azienda) {
    setWorking(azienda.id, "test");
    try {
      const data = await apiJson("/api/admin/fcic_token_test.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ azienda_id: azienda.id }),
      });
      const count = Number.isInteger(data.items_received) ? ` (${data.items_received} fornitori ricevuti)` : "";
      setFeedback((current) => ({
        ...current,
        [azienda.id]: { type: "success", text: `${data.message}${count}` },
      }));
    } catch (err) {
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "error", text: err.message } }));
    } finally {
      setWorking(azienda.id, null);
    }
  }

  async function importEntity(azienda, entity, label) {
    if (!window.confirm(`Importare ${label} da FCIC per ${azienda.nome}? I record esistenti collegati verranno aggiornati.`)) return;
    const key = `${azienda.id}-${entity}`;
    setImporting((current) => ({ ...current, [key]: true }));
    try {
      const data = await apiJson("/api/admin/fcic_import.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ azienda_id: azienda.id, entity, mode: "import" }),
      });
      setFeedback((current) => ({
        ...current,
        [azienda.id]: { type: "success", text: `${label}: ${data.received} ricevuti, ${data.created} creati, ${data.updated} aggiornati, ${data.skipped} ignorati.` },
      }));
    } catch (err) {
      setFeedback((current) => ({ ...current, [azienda.id]: { type: "error", text: err.message } }));
    } finally {
      setImporting((current) => ({ ...current, [key]: false }));
    }
  }

  if (!isSuper) {
    return <div className="p-3 text-sm text-slate-600">Sezione riservata al superadmin.</div>;
  }

  if (loading) {
    return <div className="p-4 flex items-center gap-2 text-slate-600"><Loader2 size={16} className="animate-spin" /> Caricamento integrazioni FCIC...</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2"><KeyRound size={18} /> Token FCIC</h2>
        <p className="text-sm text-slate-500">Associazione attuale: un'azienda Norma corrisponde a una location Food Cost in Cloud.</p>
      </div>

      <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 flex gap-2">
        <ShieldAlert size={18} className="shrink-0 mt-0.5" />
        <span>I token completi non vengono mai mostrati. Lascia il campo vuoto per mantenere il valore esistente.</span>
      </div>

      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <label className="relative block max-w-md">
        <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
        <input className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca azienda, FCIC client ID o piano" />
      </label>

      <div className="grid gap-3">
        {filtered.map((azienda) => {
          const working = busy[azienda.id];
          const notice = feedback[azienda.id];
          return (
            <div key={azienda.id} className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{azienda.nome}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>ID Norma: {azienda.id}</span>
                    <span>FCIC client ID: {azienda.fcic_client_id || "non associato"}</span>
                    <span>Piano: {azienda.fcic_plan_code || "non definito"}</span>
                    <span>Stato: {azienda.fcic_status || "non collegato"}</span>
                    <span>Ultimo aggiornamento: {formatDate(azienda.fcic_last_sync)}</span>
                  </div>
                  <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${azienda.token_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {azienda.token_configured ? <CheckCircle2 size={13} /> : <ShieldAlert size={13} />}
                    {azienda.token_configured ? `Token configurato ••••${azienda.token_hint || ""}` : "Token non configurato"}
                  </div>
                </div>

                <div className="w-full lg:max-w-xl">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="password"
                      autoComplete="new-password"
                      maxLength={255}
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      placeholder={azienda.token_configured ? "Inserisci solo per sostituire il token" : "Inserisci token della location"}
                      value={tokens[azienda.id] || ""}
                      onChange={(event) => setTokens((current) => ({ ...current, [azienda.id]: event.target.value }))}
                      disabled={Boolean(working)}
                    />
                    <button className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50" onClick={() => saveToken(azienda)} disabled={Boolean(working) || !(tokens[azienda.id] || "").trim()}>
                      {working === "save" ? <Loader2 size={16} className="animate-spin" /> : "Salva"}
                    </button>
                    <button className="inline-flex items-center justify-center gap-1 rounded-lg border border-sky-300 px-3 py-2 text-sm text-sky-800 disabled:opacity-50" onClick={() => testToken(azienda)} disabled={Boolean(working) || !azienda.token_configured}>
                      {working === "test" ? <Loader2 size={16} className="animate-spin" /> : <Wifi size={16} />} Verifica
                    </button>
                    <button title="Rimuovi token" className="inline-flex items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-red-700 disabled:opacity-50" onClick={() => removeToken(azienda)} disabled={Boolean(working) || !azienda.token_configured}>
                      {working === "remove" ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                  {azienda.token_configured && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[["fornitore", "Fornitori"], ["dipendente", "Dipendenti"], ["articolo", "Articoli"]].map(([entity, label]) => {
                        const key = `${azienda.id}-${entity}`;
                        return <button key={entity} className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-800 disabled:opacity-50" onClick={() => importEntity(azienda, entity, label)} disabled={Boolean(importing[key]) || Boolean(working)}>{importing[key] ? "Importazione..." : `Importa ${label}`}</button>;
                      })}
                    </div>
                  )}
                  {notice && <div className={`mt-2 text-xs ${notice.type === "success" ? "text-emerald-700" : "text-red-700"}`}>{notice.text}</div>}
                </div>
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="rounded-xl border border-dashed bg-slate-50 p-6 text-center text-sm text-slate-500">Nessuna azienda trovata.</div>}
      </div>
    </div>
  );
}
